import * as THREE from 'three'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js'
import { AppObject, OcctImportMesh, LoaderResult, LoadProgress, UvProjection } from '../types'
import { makeReporter } from './progress'
import { applyUvProjection } from '@/components/ProductProfile/utils/uvGeneration'

export async function loadSTLFiles(
  files: File[],
  uvProjection: UvProjection = 'cylindrical',
  onProgress?: (progress: LoadProgress) => void
): Promise<LoaderResult> {
  const report = makeReporter(onProgress)
  const loader = new STLLoader()
  const allGeometries: THREE.BufferGeometry[] = []

  // Load all STL files
  for (const [i, file] of files.entries()) {
    await report(files.length > 1 ? `Reading file ${i + 1} of ${files.length}` : 'Reading the file', 10 + i * 5)
    const arrayBuffer = await file.arrayBuffer()
    const geometry = loader.parse(arrayBuffer)
    allGeometries.push(geometry)
  }
  await report('Orienting the model', 45)

  // Merge all geometries to calculate overall bounding box and orientation
  const tempGroup = new THREE.Group()
  allGeometries.forEach((geom) => {
    tempGroup.add(new THREE.Mesh(geom.clone()))
  })

  const overallBox = new THREE.Box3().setFromObject(tempGroup)
  const overallSize = new THREE.Vector3()
  overallBox.getSize(overallSize)

  // Determine if rotation is needed based on overall assembly
  // Goal: Make Y-axis vertical (tallest)
  let rotationAxis: 'none' | 'x' | 'y' | 'z' = 'none'
  let rotationAngle = 0

  if (overallSize.x > overallSize.y && overallSize.x > overallSize.z) {
    // Model is laying along X axis, rotate 90° around Z to make it upright
    rotationAxis = 'z'
    rotationAngle = Math.PI / 2
  } else if (overallSize.z > overallSize.y && overallSize.z > overallSize.x) {
    // Model is laying along Z axis, rotate 90° around X to make it upright
    rotationAxis = 'x'
    rotationAngle = -Math.PI / 2
  }

  // Apply rotation to all geometries
  if (rotationAxis !== 'none') {
    const rotationMatrix = new THREE.Matrix4()
    if (rotationAxis === 'z') {
      rotationMatrix.makeRotationZ(rotationAngle)
    } else if (rotationAxis === 'x') {
      rotationMatrix.makeRotationX(rotationAngle)
    }
    allGeometries.forEach((geom) => geom.applyMatrix4(rotationMatrix))
  }

  // Recalculate bounding box after rotation
  const rotatedGroup = new THREE.Group()
  allGeometries.forEach((geom) => {
    rotatedGroup.add(new THREE.Mesh(geom.clone()))
  })
  const rotatedBox = new THREE.Box3().setFromObject(rotatedGroup)
  const rotatedCenter = new THREE.Vector3()
  rotatedBox.getCenter(rotatedCenter)

  // Center all geometries together and compute normals
  allGeometries.forEach((geometry) => {
    geometry.translate(-rotatedCenter.x, -rotatedBox.min.y, -rotatedCenter.z)
    geometry.computeVertexNormals()
  })

  await report('Generating UVs', 70)
  // Apply UV projection to all geometries after centering
  if (uvProjection !== 'none') {
    allGeometries.forEach((geometry) => {
      applyUvProjection(geometry, uvProjection)
    })
  }

  // Convert each geometry to OcctImportMesh format
  const objects: AppObject[] = []
  let totalVertexCount = 0

  allGeometries.forEach((geometry, index) => {
    const positions = geometry.getAttribute('position').array as Float32Array
    const normals = geometry.getAttribute('normal').array as Float32Array
    const uvAttribute = geometry.getAttribute('uv')
    const vertexCount = positions.length / 3
    totalVertexCount += vertexCount

    // Create sequential indices for compatibility
    const indices = new Uint32Array(vertexCount)
    for (let i = 0; i < vertexCount; i++) {
      indices[i] = i
    }

    const mesh: OcctImportMesh = {
      id: `stl-part-${index}`,
      color: [0.8, 0.8, 0.8],
      attributes: {
        position: { array: positions },
        normal: { array: normals },
        ...(uvAttribute && { uv: { array: uvAttribute.array as Float32Array } }),
      },
      index: { array: indices },
    }

    const appObject: AppObject = {
      id: `stl-part-${index}`,
      color: [0.8, 0.8, 0.8],
      mesh: mesh,
    }

    objects.push(appObject)
  })

  await report('Preparing the preview', 95)
  // Calculate viewing scale
  const finalSize = new THREE.Vector3()
  rotatedBox.getSize(finalSize)
  const viewingScale = 150 / Math.max(finalSize.x, finalSize.y, finalSize.z)

  return {
    objects,
    vertexCount: totalVertexCount,
    viewingScale,
    // Model bottom sits at y=0; aim the camera at its vertical midpoint.
    verticalCenter: (finalSize.y * viewingScale) / 2,
    unit: 'mm', // STL carries no units; millimetres is the convention
  }
}
